import React, { useState, useEffect, lazy, Suspense } from 'react';
import { isAuthenticated } from '../pages/Login/queries';
import Login from '../pages/Login/Login';
import Layout from './layout/Layout';
import PageLoader from './common/PageLoader';

// Lazy load page components
const Dashboard = lazy(() => import('../pages/Dashboard/Dashboard'));
const Alerts = lazy(() => import('../pages/Alerts'));
const Staffs = lazy(() => import('../pages/Staffs/Staffs'));
const Roles = lazy(() => import('../pages/Roles/Roles'));
const Sites = lazy(() => import('../pages/Sites/Sites'));
const Attendance = lazy(() => import('../pages/Attendance/Attendance'));
const WorkShifts = lazy(() => import('../pages/WorkShifts/WorkShifts'));

export type Page = 'dashboard' | 'alerts' | 'staffs' | 'roles' | 'sites' | 'attendance' | 'workShifts';

const AppRouter: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(isAuthenticated());
  const [currentPage, setCurrentPage] = useState<Page>('dashboard');
  const [openAddModal, setOpenAddModal] = useState(false);
  const [openStaffId, setOpenStaffId] = useState<string | undefined>(undefined);

  const navigateWithModal = (page: Page) => {
    setOpenAddModal(true);
    setCurrentPage(page);
  };

  // Reset openAddModal when navigating away from staffs/sites so normal
  // navigation back to those pages doesn't re-open the modal.
  useEffect(() => {
    if (currentPage !== 'staffs' && currentPage !== 'sites') {
      setOpenAddModal(false);
    }
    if (currentPage !== 'staffs') {
      setOpenStaffId(undefined);
    }
  }, [currentPage]);

  // Re-check auth when browser restores page from bfcache (back/forward navigation)
  useEffect(() => {
    const handlePageShow = () => setLoggedIn(isAuthenticated());
    window.addEventListener('pageshow', handlePageShow);
    return () => window.removeEventListener('pageshow', handlePageShow);
  }, []);

  // Check if user is logged in
  if (!loggedIn) {
    return <Login onLoginSuccess={() => setLoggedIn(true)} />;
  }

  // Render current page
  const renderPage = () => {
    switch (currentPage) {
      case 'dashboard':
        return <Dashboard onPageChange={navigateWithModal} />;
      case 'alerts':
        return <Alerts onNavigateToStaff={(staffId) => { setOpenStaffId(staffId); setCurrentPage('staffs'); }} />;
      case 'staffs':
        return <Staffs openAddModal={openAddModal} openStaffId={openStaffId} />;
      case 'roles':
        return <Roles />;
      case 'sites':
        return <Sites openAddModal={openAddModal} />;
      case 'attendance':
        return <Attendance />;
      case 'workShifts':
        return <WorkShifts />;
      default:
        return <Dashboard onPageChange={setCurrentPage} />;
    }
  };

  return (
    <Layout currentPage={currentPage} onPageChange={setCurrentPage}>
      <Suspense fallback={<PageLoader />}>
        {renderPage()}
      </Suspense>
    </Layout>
  );
};

export default AppRouter;